import { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { SlidersHorizontal, X, PackageSearch } from "lucide-react";
import { ProductCard } from "@/components/ProductCard";
import { Seo } from "@/components/Seo";
import { useModals } from "@/context/ModalContext";
import { api } from "@/lib/api";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export default function CollectionPage() {
  const { handle } = useParams();
  const { openConsultation } = useModals();
  const [data, setData] = useState(null);
  const [sort, setSort] = useState("");
  const [loading, setLoading] = useState(true);
  const [brands, setBrands] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setLoading(true);
    setBrands([]);
    api.getCollection(handle, sort ? { sort } : {})
      .then((d) => { setData(d); setLoading(false); })
      .catch(() => { setData({ title: "Collection", products: [] }); setLoading(false); });
  }, [handle, sort]);

  const allBrands = useMemo(
    () => [...new Set((data?.products || []).map((p) => p.brand))].sort(),
    [data]
  );

  const filtered = (data?.products || []).filter((p) => {
    if (brands.length && !brands.includes(p.brand)) return false;
    return true;
  });

  const toggleBrand = (b) =>
    setBrands((s) => (s.includes(b) ? s.filter((x) => x !== b) : [...s, b]));

  const FilterPanel = () => (
    <div className="space-y-6" data-testid="collection-filters">
      {allBrands.length > 1 && (
        <div>
          <p className="overline mb-3">Brand</p>
          <div className="space-y-2">
            {allBrands.map((b) => (
              <label key={b} className="flex items-center gap-2 text-sm text-cream/80 cursor-pointer hover:text-cream">
                <Checkbox data-testid={`collection-brand-${b.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  checked={brands.includes(b)} onCheckedChange={() => toggleBrand(b)}
                  className="border-white/30 data-[state=checked]:bg-gold data-[state=checked]:border-gold" />
                {b}
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="pt-16" data-testid="collection-page">
      {data?.title && (
        <Seo title={`${data.title} | The Recovery Shops`}
          description={`Shop the ${data.title} collection at The Recovery Shops — premium recovery equipment with expert matching and white-glove delivery. Request a quote today.`} />
      )}
      <div className="bg-charcoal border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-12">
          <p className="overline">Collection</p>
          <h1 className="font-serif text-4xl sm:text-5xl text-cream mt-2">
            {data?.title || (loading ? "Loading…" : "Collection")}
          </h1>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-10 grid lg:grid-cols-[240px_1fr] gap-10">
        <aside className="hidden lg:block"><FilterPanel /></aside>
        <div>
          <div className="flex items-center justify-between mb-6">
            <button data-testid="collection-mobile-filter" onClick={() => setShowFilters(true)}
              className="lg:hidden flex items-center gap-2 text-cream border border-white/15 px-3 py-2 text-sm">
              <SlidersHorizontal size={16} /> Filters
            </button>
            <p className="text-cream/60 text-sm">{filtered.length} products</p>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger data-testid="collection-sort" className="w-44 bg-charcoal border-white/15 text-cream">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent className="bg-charcoal border-white/15 text-cream">
                <SelectItem value="rating">Top Rated</SelectItem>
                <SelectItem value="newest">Newest</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {loading ? (
            <div className="text-cream/60 py-20 text-center">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="py-20 text-center max-w-md mx-auto" data-testid="collection-empty">
              <PackageSearch size={40} className="text-gold/70 mx-auto" />
              <h3 className="font-serif text-2xl text-cream mt-5">Nothing here just yet</h3>
              <p className="text-cream/60 mt-2">
                We couldn't find products in this collection right now. Browse the full range or let an expert help you build the perfect setup.
              </p>
              <div className="flex flex-wrap items-center justify-center gap-3 mt-6">
                <Link to="/shop" data-testid="collection-empty-shop"
                  className="bg-gold text-espresso px-6 py-3 rounded-md font-medium hover:bg-amber transition-colors">
                  Browse All Products
                </Link>
                <button data-testid="collection-empty-expert" onClick={() => openConsultation("From an empty collection")}
                  className="border border-white/20 text-cream/85 px-6 py-3 rounded-md hover:border-cream transition-colors">
                  Talk to an Expert
                </button>
              </div>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5">
              {filtered.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          )}
        </div>
      </div>

      {showFilters && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/60" onClick={() => setShowFilters(false)} />
          <div className="absolute left-0 top-0 bottom-0 w-80 bg-espresso p-6 overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-serif text-xl text-cream">Filters</h3>
              <button onClick={() => setShowFilters(false)}><X className="text-cream" /></button>
            </div>
            <FilterPanel />
            <button onClick={() => setShowFilters(false)} className="w-full mt-6 bg-gold text-espresso py-3 font-medium">
              Show {filtered.length} results
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
